import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CategoryChips from '../../../../app/components/CategoryChips';
import { mentorBookPath, paths } from '../../../../app/routes/paths';
import { useRolePath } from '../../../../app/routes/useRolePath';
import { useAppSelector } from '../../../../app/store/hooks';
import MentorCard from '../components/MentorCard';
import { useMentorCatalog } from '../hooks/useMentorCatalog';
import './MentorListPage.scss';

export default function MentorListPage() {
  const { t } = useTranslation();
  const to = useRolePath();
  const accessToken = useAppSelector((state) => state.authorization.accessToken);
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const {
    items,
    categories,
    total,
    nextCursor,
    isLoading,
    isLoadingMore,
    isError,
    loadMore,
  } = useMentorCatalog(categoryId);

  const isEmpty = !isLoading && !isError && items.length === 0;
  const showFilters = categories.length > 0;
  const showTotal = total > 0;

  return (
    <section className="mentors-page">
      <h1 className="mentors-page__title">{t('mentorCatalog.title')}</h1>

      {showTotal || showFilters ? (
        <div className="mentors-page__toolbar">
          {showTotal ? (
            <div className="mentors-page__total">
              {t('mentorCatalog.total', { count: total })}
            </div>
          ) : null}
          {showFilters ? (
            <div className="mentors-page__filters">
              <CategoryChips
                items={categories}
                value={categoryId}
                onChange={setCategoryId}
                ariaLabel={t('mentorCatalog.filters')}
                clearLabel={t('mentorCatalog.clear')}
              />
            </div>
          ) : null}
        </div>
      ) : null}

      {isLoading ? <p className="mentors-page__status">{t('mentorCatalog.loading')}</p> : null}
      {isError ? <p className="mentors-page__status">{t('mentorCatalog.error')}</p> : null}

      {isEmpty ? (
        <div className="mentors-page__invite" role="status">
          <p className="mentors-page__invite-title">
            {categoryId
              ? t('mentorCatalog.emptyFilter')
              : t('mentorCatalog.emptyTitle')}
          </p>
          {!categoryId ? (
            <>
              <p className="mentors-page__invite-text">
                {t('mentorCatalog.emptyInvite')}
              </p>
              <Link className="mentors-page__invite-cta" to={paths.signUp}>
                {t('mentorCatalog.emptyCta')}
              </Link>
            </>
          ) : null}
        </div>
      ) : null}

      <ul className="mentors-page__grid">
        {items.map((mentor) => (
          <li key={mentor.id}>
            <MentorCard
              name={mentor.displayName}
              headline={mentor.headline}
              imageUrl={mentor.avatarUrl}
              categories={mentor.categories.map((category) => ({
                id: category.id,
                title: category.name,
              }))}
              price={mentor.price}
              to={
                accessToken
                  ? to(mentorBookPath(mentor.slug))
                  : mentor.publicPath || `/public/mentor/${mentor.slug}`
              }
            />
          </li>
        ))}
      </ul>

      {nextCursor ? (
        <div className="mentors-page__more">
          <button type="button" onClick={loadMore} disabled={isLoadingMore}>
            {isLoadingMore ? t('mentorCatalog.loading') : t('mentorCatalog.more')}
          </button>
        </div>
      ) : null}
    </section>
  );
}
